// File: app/login/forgot-password/page.tsx
"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { FaCheck, FaTimes, FaEye, FaEyeSlash, FaSyncAlt } from "react-icons/fa";

type Step = "phone" | "code" | "password";

// تبدیل ارقام فارسی/عربی به لاتین
function normalizeDigits(input: string): string {
  if (!input) return "";
  const persianZero = "۰".charCodeAt(0);
  const arabicZero  = "٠".charCodeAt(0);
  let out = "";
  for (const ch of input) {
    const code = ch.charCodeAt(0);
    if (code >= 0x06F0 && code <= 0x06F9) out += String.fromCharCode(code - persianZero + 48);
    else if (code >= 0x0660 && code <= 0x0669) out += String.fromCharCode(code - arabicZero + 48);
    else out += ch;
  }
  return out;
}

// تشخیص کاراکتر غیر ASCII چاپ‌پذیر (نشانه‌ی کیبورد فارسی)
const containsNonAsciiPrintable = (s: string) => /[^\x20-\x7E]/.test(s);

export default function ForgotPasswordPage() {
  const [step, setStep] = useState<Step>("phone");

  // Step 1: phone + CAPTCHA
  const [mobilePhone, setMobilePhone] = useState("");
  const [captchaId, setCaptchaId] = useState<string | null>(null);
  const [captchaSrc, setCaptchaSrc] = useState<string | null>(null);
  const [captchaInput, setCaptchaInput] = useState("");
  const [sending, setSending] = useState(false);

  // Step 2: code
  const [otp, setOtp] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [resendLeft, setResendLeft] = useState(0);

  // Step 3: password
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // UX
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [finished, setFinished] = useState(false);

  // خطای کیبورد فارسی (برای نمایش پیام و جلوگیری از ثبت)
  const [kbNonEnglish, setKbNonEnglish] = useState(false);

  // قوانین رمز (اجباری‌ها)
  const hasMinLength = newPassword.length >= 8;
  const hasUpper     = /[A-Z]/.test(newPassword);
  const hasLower     = /[a-z]/.test(newPassword);
  const hasDigit     = /[0-9]/.test(newPassword);
  const asciiOnly    = /^[\x21-\x7E]+$/.test(newPassword) || newPassword.length === 0; // فقط کاراکترهای انگلیسی مجاز (Space مجاز نیست)
  const rulesOk      = hasMinLength && hasUpper && hasLower && hasDigit && asciiOnly && !kbNonEnglish;

  // سایر اعتبارسنجی‌ها
  const phoneValid     = /^0\d{10}$/.test(mobilePhone);
  const captchaLenOk   = captchaInput.length === 4; // صحت واقعی در سرور چک می‌شود
  const otpLenOk       = otp.length === 6;
  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;

  useEffect(() => {
    initCaptcha();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let t: number | undefined;
    if (resendLeft > 0) t = window.setTimeout(() => setResendLeft((s) => s - 1), 1000);
    return () => { if (t) window.clearTimeout(t); };
  }, [resendLeft]);

  // Helpers
  const parseJsonSafe = async (res: Response) => {
    const text = await res.text();
    try { return JSON.parse(text || "{}"); } catch { return {}; }
  };

  async function initCaptcha() {
    try {
      setCaptchaInput("");
      const res  = await fetch("/api/login/forgot-password/captcha", { method: "POST" });
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.captchaId) throw new Error("CAPTCHA init failed");
      setCaptchaId(data.captchaId);
      setCaptchaSrc(`/api/login/forgot-password/captcha?cid=${encodeURIComponent(data.captchaId)}&t=${Date.now()}`);
    } catch {
      // تلاش بعدی با «کد جدید»
    }
  }
  const refreshCaptcha = async () => { await initCaptcha(); };

  // onChange
  const onPhone   = (v: string) => setMobilePhone(normalizeDigits(v).replace(/[^0-9]/g, "").slice(0, 11));
  const onCaptcha = (v: string) => setCaptchaInput(normalizeDigits(v).replace(/[^0-9]/g, "").slice(0, 4));
  const onOtp     = (v: string) => setOtp(normalizeDigits(v).replace(/[^0-9]/g, "").slice(0, 6));

  // رمز جدید: هم‌زمان تشخیص کیبورد فارسی + پاکسازی کاراکترهای غیر ASCII
  const onNew = (v: string) => {
    const raw = v;
    const nonEng = containsNonAsciiPrintable(raw); // هر چیزی خارج از ASCII چاپ‌پذیر
    setKbNonEnglish(nonEng);
    const sanitized = raw.replace(/[^\x20-\x7E]/g, "");
    setNewPassword(sanitized);
  };
  const onConfirm = (v: string) => {
    const sanitized = v.replace(/[^\x20-\x7E]/g, "");
    setConfirmPassword(sanitized);
  };

  // API: ارسال کد
  const sendCode = async () => {
    setError(""); setSuccess("");
    if (!phoneValid) { setError("شماره موبایل باید ۱۱ رقمی و با 0 شروع شود."); return; }
    if (!captchaId || !captchaLenOk) { setError("کپچا را کامل وارد کنید."); return; }

    setSending(true);
    try {
      const res = await fetch("/api/login/forgot-password/send-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobilePhone, captchaId, captchaInput }),
      });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data?.message || "خطا در ارسال کد");
      setSuccess("کد تایید ارسال شد.");
      setStep("code");
      setResendLeft(60);
    } catch (e: any) {
      setError(e.message || "خطا در ارسال کد");
      await refreshCaptcha(); // کپچای جدید برای تلاش بعدی
    } finally {
      setSending(false);
    }
  };

  // API: تأیید کد
  const verifyCode = async () => {
    setError(""); setSuccess("");
    if (!phoneValid) { setError("شماره موبایل صحیح نیست."); return; }
    if (!otpLenOk)   { setError("کد تایید ۶ رقمی را وارد کنید."); return; }

    setVerifying(true);
    try {
      const res = await fetch("/api/login/forgot-password/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobilePhone, otp }),
      });
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.resetToken) throw new Error(data?.message || "کد اشتباه است.");
      setResetToken(data.resetToken);
      setStep("password");
      setSuccess("تایید شد، رمز جدید را وارد کنید.");
    } catch (e: any) {
      setError(e.message || "کد اشتباه است.");
    } finally {
      setVerifying(false);
    }
  };

  // API: ثبت رمز
  const submitPassword = async () => {
    setError(""); setSuccess("");

    // اگر کیبورد فارسی باشد یا کاراکتر غیرمجاز وارد شده باشد
    if (kbNonEnglish || !asciiOnly) {
      setError("لطفاً زبان کیبورد را به انگلیسی تغییر دهید و فقط از کاراکترهای مجاز استفاده کنید.");
      return;
    }
    if (!rulesOk) {
      const missing: string[] = [];
      if (!hasMinLength) missing.push("حداقل ۸ کاراکتر");
      if (!hasUpper)     missing.push("حرف بزرگ");
      if (!hasLower)     missing.push("حرف کوچک");
      if (!hasDigit)     missing.push("عدد");
      setError(`این موارد هنوز کامل نیست: ${missing.join("، ")}`);
      return;
    }
    if (!passwordsMatch) { setError("تکرار رمز با رمز یکسان نیست."); return; }
    if (!resetToken)     { setError("سشن بازیابی معتبر نیست. دوباره تلاش کنید."); return; }

    setSubmitting(true);
    try {
      const res = await fetch("/api/login/forgot-password/reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobilePhone, resetToken, newPassword }),
      });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data?.message || "خطا در ثبت رمز");

      setSuccess("رمز با موفقیت تغییر کرد. اکنون می‌توانید وارد شوید.");
      setFinished(true);
    } catch (e: any) {
      setError(e.message || "خطا در ثبت رمز");
    } finally {
      setSubmitting(false);
    }
  };

  const Rule = ({ ok, text }: { ok: boolean; text: string }) => (
    <div className={`flex items-center gap-2 text-sm ${ok ? "text-green-600" : "text-slate-300"}`}>
      {ok ? <FaCheck /> : <FaTimes />} <span>{text}</span>
    </div>
  );

  // ---------- UI (هماهنگ با لاگین: همان پس‌زمینه، کارت و دکمه‌ی اصلیِ برند)
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-900 to-slate-950 p-4 md:p-10">
      <div className="w-full max-w-md bg-slate-900/90 backdrop-blur-md p-8 md:p-10 rounded-[2rem] shadow-2xl border border-slate-700 text-right space-y-5 text-slate-200">
        <h1 className="text-2xl font-black text-center text-slate-200">بازیابی رمز عبور</h1>

        {error   && <div className="p-3 rounded border border-red-500/30 bg-red-500/10 text-red-200">{error}</div>}
        {success && <div className="p-3 rounded border border-green-500/30 bg-green-500/10 text-green-200">{success}</div>}

        {finished ? (
          <div className="mt-2">
            <Link
              href="/login"
              className="inline-flex items-center justify-center w-full p-3 rounded-xl bg-brand-600 text-white border border-brand-500 shadow-lg shadow-brand-600/30 hover:bg-brand-500 transition-colors"
            >
              بازگشت به صفحه ورود
            </Link>
          </div>
        ) : (
          <>
            {step === "phone" && (
              <>
                <label className="block mb-2 text-slate-200">شماره موبایل</label>
                <input
                  dir="ltr"
                  type="tel"
                  inputMode="numeric"
                  placeholder="مثلاً 09123456789"
                  value={mobilePhone}
                  onChange={(e) => onPhone(e.target.value)}
                  autoComplete="off"
                  className="w-full p-3 border border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 text-slate-100"
                />

                {/* CAPTCHA تصویری با Next/Image */}
                <div className="mt-4">
                  <label className="block mb-2 text-slate-200">کپچا</label>
                  <div className="flex items-center gap-3">
                    {captchaSrc ? (
                      <Image
                        src={captchaSrc}
                        alt=""
                        width={140}
                        height={48}
                        unoptimized
                        draggable={false}
                        aria-hidden="true"
                        className="select-none pointer-events-none border border-slate-700 rounded bg-slate-900"
                        style={{ imageRendering: "pixelated" }}
                      />
                    ) : (
                      <div className="px-4 py-2 rounded border border-slate-700 bg-slate-900 text-slate-300 select-none">
                        در حال بارگذاری…
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={refreshCaptcha}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
                      aria-label="refresh captcha"
                      title="کد جدید"
                    >
                      <FaSyncAlt className="opacity-80" />
                      کد جدید
                    </button>
                  </div>
                  <input
                    dir="ltr"
                    type="text"
                    inputMode="numeric"
                    placeholder="کپچا ۴ رقمی"
                    value={captchaInput}
                    onChange={(e) => onCaptcha(e.target.value)}
                    autoComplete="off"
                    className="mt-3 w-full p-3 border border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 text-slate-100"
                  />
                </div>

                <button
                  onClick={sendCode}
                  disabled={sending || !phoneValid || !captchaId || !captchaLenOk}
                  className="mt-4 w-full p-3 rounded-xl bg-brand-600 text-white border border-brand-500 shadow-lg shadow-brand-600/30 hover:bg-brand-500 transition-colors disabled:opacity-60"
                >
                  {sending ? "در حال ارسال..." : "ارسال کد تأیید"}
                </button>

                {/* دکمه/لینک برگشت مثل صفحه ثبت شماره: زیر دکمه ثبت */}
                <div className="mt-4 text-center">
                  <Link href="/login" className="inline-block text-sm text-slate-200 hover:text-slate-100">
                    ← بازگشت به صفحه ورود
                  </Link>
                </div>
              </>
            )}

            {step === "code" && (
              <>
                <label className="block mb-2 text-slate-200">کد تأیید ۶ رقمی</label>
                <input
                  dir="ltr"
                  type="text"
                  inputMode="numeric"
                  placeholder="------"
                  value={otp}
                  onChange={(e) => onOtp(e.target.value)}
                  autoComplete="off"
                  className="w-full p-3 border border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 tracking-widest text-slate-100"
                />
                <button
                  onClick={verifyCode}
                  disabled={verifying || !otpLenOk}
                  className="mt-4 w-full p-3 rounded-xl bg-brand-600 text-white border border-brand-500 shadow-lg shadow-brand-600/30 hover:bg-brand-500 transition-colors disabled:opacity-60"
                >
                  {verifying ? "در حال بررسی..." : "تأیید کد"}
                </button>

                <button
                  onClick={async () => { if (resendLeft === 0) { await refreshCaptcha(); setStep("phone"); } }}
                  disabled={resendLeft > 0}
                  className="mt-2 w-full p-3 bg-slate-800 text-slate-100 rounded-xl border border-slate-700 disabled:opacity-60"
                >
                  {resendLeft > 0 ? `ارسال مجدد کد تا ${resendLeft} ثانیه` : "ارسال مجدد کد"}
                </button>

                {/* لینک برگشت زیر دکمه‌ها */}
                <div className="mt-4 text-center">
                  <Link href="/login" className="inline-block text-sm text-slate-200 hover:text-slate-100">
                    ← بازگشت به صفحه ورود
                  </Link>
                </div>
              </>
            )}

            {step === "password" && (
              <>
                {/* فیلد اول: رمز جدید + قوانین دقیقاً زیر آن */}
                <label className="block mb-2 text-slate-200">رمز جدید</label>
                <div className="relative">
                  <input
                    dir="ltr"
                    type={showNew ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => onNew(e.target.value)}
                    autoComplete="new-password"
                    className="w-full p-3 pr-10 border border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 text-slate-100"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300"
                    aria-label="toggle password"
                  >
                    {showNew ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>

                {/* هشدار کیبورد فارسی */}
                {kbNonEnglish && (
                  <div className="mt-2 text-sm text-red-600">
                    لطفاً زبان کیبورد را انگلیسی کنید (Only English letters, numbers and symbols).
                  </div>
                )}

                {/* قوانین زیر فیلد اول */}
                <div className="mt-3 space-y-1">
                  <Rule ok={hasMinLength} text="حداقل ۸ کاراکتر" />
                  <Rule ok={hasUpper}   text="حداقل یک حرف بزرگ انگلیسی (A…Z)" />
                  <Rule ok={hasLower}   text="حداقل یک حرف کوچک انگلیسی (a…z)" />
                  <Rule ok={hasDigit}   text="حداقل یک عدد انگلیسی (0…9)" />
                  <Rule ok={asciiOnly && !kbNonEnglish} text="فقط کاراکترهای مجاز انگلیسی (Symbols اختیاری)" />
                </div>

                {/* فیلد دوم: تکرار رمز + پیام تطابق دقیقاً زیر آن */}
                <label className="block mt-5 mb-2 text-slate-200">تکرار رمز جدید</label>
                <div className="relative">
                  <input
                    dir="ltr"
                    type={showConfirm ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => onConfirm(e.target.value)}
                    autoComplete="new-password"
                    className="w-full p-3 pr-10 border border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 text-slate-100"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300"
                    aria-label="toggle confirm"
                  >
                    {showConfirm ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>

                <div className={`mt-2 text-sm ${passwordsMatch ? "text-green-600" : "text-slate-300"}`}>
                  {confirmPassword
                    ? (passwordsMatch ? "تایید رمز مطابقت دارد" : "تکرار رمز باید با رمز برابر باشد")
                    : "تکرار رمز باید با رمز برابر باشد"}
                </div>

                <button
                  onClick={submitPassword}
                  disabled={submitting || !rulesOk || !passwordsMatch || kbNonEnglish}
                  className="mt-4 w-full p-3 rounded-xl bg-brand-600 text-white border border-brand-500 shadow-lg shadow-brand-600/30 hover:bg-brand-500 transition-colors disabled:opacity-60"
                >
                  {submitting ? "در حال ثبت..." : "ثبت رمز جدید"}
                </button>

                {/* لینک برگشت زیر دکمه ثبت */}
                <div className="mt-4 text-center">
                  <Link href="/login" className="inline-block text-sm text-slate-200 hover:text-slate-100">
                    ← بازگشت به صفحه ورود
                  </Link>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
